import React from "react";
import { useField } from "formik";
import classNames from "classnames";
import {
    FEDERAL_TAX_CLASSIFICATION_OPTIONS,
    FederalTaxClassification,
    getFederalTaxClassificationLabel,
} from "@common/types/counterparty";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Card } from "@design-system/Card/Card";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import type { FormSchema } from "./counterpartyFormSchema";
import styles from "./CounterpartyForm.module.scss";

const fields = createFormikFields<FormSchema>();

interface FormSectionCardProps {
    title: string;
    /** Id on the heading, for a field it names through `aria-labelledby`. */
    titleId?: string;
    fullWidth?: boolean;
    sectionClassName?: string;
    children: React.ReactNode;
}

export const FormSectionCard: React.FC<FormSectionCardProps> = ({
    title,
    titleId,
    fullWidth,
    sectionClassName,
    children,
}) => (
    <section className={classNames(styles.section, sectionClassName)}>
        <h4 id={titleId}>{title}</h4>
        <Card>
            <div
                className={classNames({
                    [styles.narrowContainer]: !fullWidth,
                })}
            >
                {children}
            </div>
        </Card>
    </section>
);

export const Eligible1099Field: React.FC = () => {
    const [field, , helpers] = useField<boolean | null>("isEligible1099");

    return (
        <VStack gap={4} role="group" aria-label="1099 eligible">
            <span className={styles.groupLabel}>1099 eligible</span>
            <Checkbox
                checked={field.value === true}
                onCheckedChange={() => {
                    helpers.setValue(field.value === true ? null : true);
                }}
                label="Mark as 1099 eligible"
            />
        </VStack>
    );
};

/** Label-less: the "Note" section heading names it through `aria-labelledby`. */
export const NoteField = ({
    "aria-labelledby": ariaLabelledby,
}: {
    "aria-labelledby": string;
}) => (
    <fields.TextArea
        name="note"
        aria-labelledby={ariaLabelledby}
        showErrorMessage={false}
        className={styles.textField}
        size="lg"
        autoGrow
        minRows={4}
        placeholder="Add a note"
    />
);

export const FederalTaxClassificationField: React.FC = () => (
    <FormikField<FederalTaxClassification | null>
        name="federalTaxClassification"
        label="Federal tax classification"
        control="button"
        showErrorMessage={false}
    >
        {({ field, helpers }) => {
            const selectClassification = (
                classification: FederalTaxClassification | null,
            ) => {
                helpers.setValue(classification);
                helpers.setTouched(true);
            };
            return (
                <Picker.Select
                    value={field.value}
                    onValueChange={selectClassification}
                >
                    <Picker.ButtonTrigger
                        render={<Picker.Button variant="primary" size="lg" />}
                        value={getFederalTaxClassificationLabel(field.value)}
                    />
                    <Picker.Popup>
                        <Picker.List>
                            {FEDERAL_TAX_CLASSIFICATION_OPTIONS.map((option) =>
                                option.value === null ? (
                                    <Picker.Item
                                        key="not-specified"
                                        onSelect={() =>
                                            selectClassification(null)
                                        }
                                    >
                                        {option.label}
                                    </Picker.Item>
                                ) : (
                                    <Picker.Item
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </Picker.Item>
                                ),
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            );
        }}
    </FormikField>
);
